// Password utilities: validation, strength, and secure generation

import i18n from '../i18n';

export type PasswordValidation = {
  valid: boolean;
  errors: string[];
  message?: string;
};

type Rule = (password: string) => string | null;

// Data-driven password rules to keep logic simple and reusable
export const passwordRules: Rule[] = [
  (p) => (p.length < 8 ? i18n.t('password.minLength') : null),
  (p) => (!/[a-z]/.test(p) ? i18n.t('password.lowercase') : null),
  (p) => (!/[A-Z]/.test(p) ? i18n.t('password.uppercase') : null),
  (p) => (!/\d/.test(p) ? i18n.t('password.number') : null),
  (p) => (!/[!@#$%^&*(),.?":{}|<>]/.test(p) ? i18n.t('password.special') : null),
];

export function validatePassword(password: string): PasswordValidation {
  const errors = passwordRules.map((rule) => rule(password)).filter((e): e is string => !!e);
  return {
    valid: errors.length === 0,
    errors,
    message: errors[0],
  };
}

// Simple strength helper aligned with current UI: weak/strong
export function getPasswordStrength(password: string): 'weak' | 'strong' | 'empty' {
  if (!password) return 'empty';
  return validatePassword(password).valid ? 'strong' : 'weak';
}

// Secure random password generator using Web Crypto
export function generatePassword(length: number = 12): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*';
  const all = upper + lower + digits + special;

  const pick = (charset: string): string => charset[randomInt(charset.length)];

  const result: string[] = [];
  // Ensure at least one of each required type
  result.push(pick(upper));
  result.push(pick(lower));
  result.push(pick(digits));
  result.push(pick(special));

  for (let i = result.length; i < length; i++) {
    result.push(pick(all));
  }

  // Fisher–Yates shuffle using crypto randomness
  for (let i = result.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result.join('');
}

function randomInt(maxExclusive: number): number {
  // Web Crypto in browsers provides crypto.getRandomValues
  const array = new Uint32Array(1);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(array);
    return array[0] % maxExclusive;
  }
  // Fallback: still avoid Math.random in normal paths, but provide a backup
  return Math.floor(Math.random() * maxExclusive);
}
